import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { isApiError } from '@/api/errors';
import { archivePlan, createPlan, listPlans, updatePlan } from '@/api/plans';
import type { PlanLimitsInput, PlanView } from '@/api/types';
import { useCan } from '@/auth/session';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { PageHeader } from '@/ui/PageHeader';
import { PageSpinner } from '@/ui/Spinner';
import { QueryError } from '@/ui/QueryError';

const LIMIT_FIELDS = [
  { key: 'social_accounts', label: 'Social accounts' },
  { key: 'posts_per_month', label: 'Posts per month' },
  { key: 'members', label: 'Members' },
  { key: 'workspaces', label: 'Workspaces' },
] as const;

export function PlansPage() {
  const can = useCan();
  const queryClient = useQueryClient();
  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<PlanView | null>(null);

  const query = useQuery({ queryKey: ['plans'], queryFn: listPlans });

  const archive = useMutation({
    mutationFn: archivePlan,
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['plans'] }),
  });

  if (query.isPending) return <PageSpinner label="Loading plans" />;
  if (query.isError) return <QueryError error={query.error} onRetry={() => void query.refetch()} />;

  const canManage = can('plans.manage');

  return (
    <>
      <PageHeader
        title="Plans"
        actions={
          canManage && (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>New plan</Button>
              </DialogTrigger>
              <PlanFormDialog mode="create" onDone={() => setCreateOpen(false)} />
            </Dialog>
          )
        }
      />

      <PlanGroup
        title="Platform plans"
        plans={query.data.platform_plans}
        canManage={canManage}
        onArchive={(plan) => archive.mutate(plan.id)}
        onEdit={setEditing}
      />
      <PlanGroup
        title="Partner plans"
        plans={query.data.partner_plans}
        canManage={canManage}
        onArchive={(plan) => archive.mutate(plan.id)}
        onEdit={setEditing}
      />

      <Dialog open={editing !== null} onOpenChange={(open) => !open && setEditing(null)}>
        {editing && <PlanFormDialog mode="edit" plan={editing} onDone={() => setEditing(null)} />}
      </Dialog>
    </>
  );
}

function PlanGroup({
  title,
  plans,
  canManage,
  onArchive,
  onEdit,
}: {
  title: string;
  plans: PlanView[];
  canManage: boolean;
  onArchive: (plan: PlanView) => void;
  onEdit: (plan: PlanView) => void;
}) {
  if (plans.length === 0) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {plans.map((plan) => (
          <div key={plan.id} className="flex flex-col gap-2 rounded-lg border p-3">
            <div className="flex items-center justify-between">
              <span className="font-medium">{plan.name}</span>
              <Badge variant={plan.status === 'ACTIVE' ? 'secondary' : 'outline'}>
                {plan.status}
              </Badge>
            </div>
            <span className="text-xs text-muted-foreground">{plan.code}</span>
            <dl className="grid grid-cols-2 gap-x-2 gap-y-1 text-sm">
              {LIMIT_FIELDS.map(({ key, label }) => (
                <div key={key} className="contents">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd>{plan.limits[key] ?? 'Unlimited'}</dd>
                </div>
              ))}
            </dl>
            {canManage && plan.editable && plan.status === 'ACTIVE' && (
              <div className="flex gap-2 pt-1">
                <Button variant="outline" size="sm" onClick={() => onEdit(plan)}>
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => onArchive(plan)}>
                  Archive
                </Button>
              </div>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  );
}

const limitField = z
  .string()
  .trim()
  .refine(
    (value) => value === '' || (/^\d+$/.test(value) && Number(value) >= 1),
    'Enter a whole number of 1 or more, or leave blank for unlimited.',
  );

const planFormSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Code is required.')
    .max(60, 'Code is too long.')
    .regex(/^[a-z0-9]+(_[a-z0-9]+)*$/, 'Lowercase letters, digits and single underscores only.'),
  name: z.string().trim().min(1, 'Name is required.').max(200, 'Name is too long.'),
  social_accounts: limitField,
  posts_per_month: limitField,
  members: limitField,
  workspaces: limitField,
});
type PlanForm = z.infer<typeof planFormSchema>;

function toLimits(form: PlanForm): PlanLimitsInput {
  const parse = (value: string) => (value === '' ? null : Number(value));
  return {
    social_accounts: parse(form.social_accounts),
    posts_per_month: parse(form.posts_per_month),
    members: parse(form.members),
    workspaces: parse(form.workspaces),
  };
}

function fromLimits(
  limits: PlanLimitsInput,
): Pick<PlanForm, 'social_accounts' | 'posts_per_month' | 'members' | 'workspaces'> {
  const format = (value: number | null) => (value === null ? '' : String(value));
  return {
    social_accounts: format(limits.social_accounts),
    posts_per_month: format(limits.posts_per_month),
    members: format(limits.members),
    workspaces: format(limits.workspaces),
  };
}

function PlanFormDialog(
  props:
    { mode: 'create'; onDone: () => void } | { mode: 'edit'; plan: PlanView; onDone: () => void },
) {
  const { mode, onDone } = props;
  const plan = props.mode === 'edit' ? props.plan : undefined;
  const queryClient = useQueryClient();
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<PlanForm>({
    resolver: zodResolver(planFormSchema),
    defaultValues: plan
      ? { code: plan.code, name: plan.name, ...fromLimits(plan.limits) }
      : {
          code: '',
          name: '',
          social_accounts: '',
          posts_per_month: '',
          members: '',
          workspaces: '',
        },
  });

  const save = useMutation({
    mutationFn: (values: PlanForm) =>
      props.mode === 'create'
        ? createPlan({ code: values.code, name: values.name, limits: toLimits(values) })
        : updatePlan(props.plan.id, { name: values.name, limits: toLimits(values) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['plans'] });
      onDone();
    },
  });

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>{mode === 'create' ? 'New plan' : `Edit ${plan?.name}`}</DialogTitle>
      </DialogHeader>

      {save.isError && (
        <Alert variant="destructive">
          <AlertDescription>
            {isApiError(save.error) ? save.error.message : 'Could not save the plan.'}
          </AlertDescription>
        </Alert>
      )}

      <form id="plan-form" onSubmit={handleSubmit((values) => save.mutate(values))}>
        <FieldGroup>
          <Field data-invalid={Boolean(errors.code)}>
            <FieldLabel htmlFor="plan-code">Code</FieldLabel>
            <Input id="plan-code" disabled={mode === 'edit'} {...register('code')} />
            <FieldError errors={[errors.code]} />
          </Field>
          <Field data-invalid={Boolean(errors.name)}>
            <FieldLabel htmlFor="plan-name">Name</FieldLabel>
            <Input id="plan-name" autoFocus {...register('name')} />
            <FieldError errors={[errors.name]} />
          </Field>
          {LIMIT_FIELDS.map(({ key, label }) => (
            <Field key={key} data-invalid={Boolean(errors[key])}>
              <FieldLabel htmlFor={`plan-${key}`}>{label}</FieldLabel>
              <Input id={`plan-${key}`} placeholder="Unlimited" {...register(key)} />
              <FieldError errors={[errors[key]]} />
            </Field>
          ))}
        </FieldGroup>
      </form>

      <DialogFooter>
        <Button type="submit" form="plan-form" disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
